import React, { useState, useEffect, Suspense, useRef, lazy } from 'react'
import { storeImage, processImageUrl } from './utils/imageStorage'
import { Routes, Route, useLocation, Link } from 'react-router-dom'
import './App.css'
import VapiCall from './components/VapiCall.jsx'
import Button from './components/Button.jsx'
import CallTransition from './components/CallTransition.jsx'
import { withDevTools, createDebugger, trackUserJourney } from './utils/debugConfig'
import { getAttorneyConfigAsync } from './config/attorneys'
import { DEFAULT_ASSISTANT_ID } from './constants/vapiConstants'
import MapView from './components/MapView.jsx'
import AttorneyDossier from './components/AttorneyDossier.jsx'
import CallSummary from './components/CallSummary.jsx'
import { getCurrentSubdomain } from './utils/subdomainTester'
import Navbar from './components/Navbar.jsx'
import Dashboard from './pages/DashboardNew.jsx'
import GlobeDossierView from './components/GlobeDossierView.jsx'
import TestSubdomains from './components/TestSubdomains.jsx'
import AnimatedBackground from './components/AnimatedBackground.jsx'
import ThemeToggle from './components/ThemeToggle.jsx'
import SignInButton from './components/SignInButton.jsx'
import AboutPage from './pages/AboutPage'
import SimplifiedPreview from "./components/SimplifiedPreview";
import EnhancedPreviewNew from "./components/preview/EnhancedPreviewNew";
import AuthOverlay from './components/AuthOverlay';
import AuthCallback from './pages/AuthCallback';
import CompleteProfile from './pages/CompleteProfile';
import CrmDemo from './pages/CrmDemo';
import { testSupabaseConnection, logSupabaseConfig } from './testSupabase';
import { initializeSupabaseConfig, verifySupabaseConfig } from './utils/supabaseConfigVerifier';
import TestComponent from './components/TestComponent';
import SubdomainTestPage from './pages/SubdomainTestPage';

// Create debugger for App component
const debug = createDebugger('App');

// Lazy load components
const PreviewPage = lazy(() => import('./pages/PreviewPage'));
const SimpleDemoPage = lazy(() => import('./pages/SimpleDemoPage'));

// Home component to contain the main app content
const Home = ({ isLoading, callActive, showAttorneyInfo, showCallSummary, attorneyProfile, startCall, endCall, callData, subdomain, setShowAttorneyInfo, setShowCallSummary, buttonText, isAttorneySubdomain }) => {
  const [showTransition, setShowTransition] = useState(false);
  const [buttonPosition, setButtonPosition] = useState(null);
  const buttonRef = useRef(null);

  // Function to handle the button click and start the transition
  const handleStartCall = () => {
    // Get the button position for the transition animation
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setButtonPosition({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height
      });
    }

    // Show the transition
    setShowTransition(true);

    // Track the call start
    debug.log('Call transition started', { timestamp: new Date().toISOString() });
    trackUserJourney('call_transition_started');
  };

  // Function to handle transition completion
  const handleTransitionComplete = () => {
    // Actually start the call after the transition completes
    startCall();

    // Hide the transition overlay
    setTimeout(() => {
      setShowTransition(false);
    }, 500);
  };

  // If this is an attorney subdomain and we have a valid attorney profile, render the attorney's preview
  if (isAttorneySubdomain && attorneyProfile && !isLoading && attorneyProfile.firmName) {
    return (
      <div className="attorney-subdomain-preview">
        <SimplifiedPreview
          // Basic information
          firmName={attorneyProfile.firmName}
          logoUrl={attorneyProfile.logo || "/PRIMARY CLEAR.png"} // This will be used for the banner at the top
          mascot={attorneyProfile.mascot || "/PRIMARY CLEAR.png"} // This is kept for backward compatibility
          buttonImageUrl={attorneyProfile.buttonImage || attorneyProfile.mascot || "/PRIMARY CLEAR.png"} // Use buttonImage for the button if available
          // Log the image URLs for debugging
          _debug_logo={console.log('App.jsx passing logo URL:', attorneyProfile.logo)}
          _debug_button={console.log('App.jsx passing button image URL:', attorneyProfile.buttonImage)}

          // Colors and styling
          primaryColor={attorneyProfile.primaryColor || '#4B74AA'}
          secondaryColor={attorneyProfile.secondaryColor || '#2C3E50'}
          buttonColor={attorneyProfile.buttonColor || '#3498db'}
          backgroundColor={attorneyProfile.backgroundColor || '#1a1a1a'}
          backgroundOpacity={attorneyProfile.backgroundOpacity || 0.9}
          buttonText={attorneyProfile.buttonText || "Start Consultation"}
          buttonOpacity={attorneyProfile.buttonOpacity || 1}
          practiceAreaBackgroundOpacity={attorneyProfile.practiceAreaBackgroundOpacity || 0.1}
          textBackgroundColor={attorneyProfile.textBackgroundColor || '#634C38'}

          // Content
          practiceDescription={attorneyProfile.practiceDescription || ''}
          welcomeMessage={attorneyProfile.welcomeMessage || `Hello, I'm Scout from ${attorneyProfile.firmName}. How can I help you today?`}
          informationGathering={attorneyProfile.informationGathering || ''}
          officeAddress={attorneyProfile.officeAddress || ''}
          schedulingLink={attorneyProfile.schedulingLink || ''}
          practiceAreas={attorneyProfile.practiceAreas || []}

          // Vapi configuration
          vapiInstructions={attorneyProfile.vapiInstructions || "You are a general legal assistant. Collect basic information about the client's legal needs and help them find an appropriate attorney."}
          vapiContext={attorneyProfile.vapiContext || ""}
          vapi_assistant_id={attorneyProfile.vapi_assistant_id}
          voiceId={attorneyProfile.voiceId || null}
          aiModel={attorneyProfile.aiModel || null}

          // Theme
          theme={'dark'}
        />
      </div>
    );
  }

  // Otherwise, render the regular home page
  return (
    <>
      {isLoading ? (
        <div className="loading-indicator">Loading...</div>
      ) : (
        <>
          {!callActive && !showAttorneyInfo && !showCallSummary && attorneyProfile && (
            <div className="start-button-container" ref={buttonRef}>
              <Button
                onClick={handleStartCall}
                label="Get Started"
                mascot="/PRIMARY CLEAR.png"
                isLoading={false}
              />
            </div>
          )}

          {/* Call Transition Animation */}
          <CallTransition
            isActive={showTransition}
            onTransitionComplete={handleTransitionComplete}
            buttonPosition={buttonPosition}
            mascotUrl="/PRIMARY CLEAR.png"
          />

          {callActive && (
            <div className={`call-card-container ${callActive ? 'active' : ''}`}>
              <div className="call-card">
                <VapiCall
                  onEndCall={endCall}
                  subdomain={subdomain}
                  customInstructions={null}
                  assistantId={DEFAULT_ASSISTANT_ID} // Always use the default assistant ID for the home page
                  forceDefaultAssistant={true} // Force using the default assistant
                />
              </div>
            </div>
          )}

          <HomeContent
            showAttorneyInfo={showAttorneyInfo}
            callData={callData}
            showCallSummary={showCallSummary}
            setShowAttorneyInfo={setShowAttorneyInfo}
            setShowCallSummary={setShowCallSummary}
          />
        </>
      )}
    </>
  );
};

// Home component continued
const HomeContent = ({ showAttorneyInfo, callData, showCallSummary, setShowAttorneyInfo, setShowCallSummary }) => (
  <>
    {showAttorneyInfo && callData && (
      <div className="attorney-info-container">
        <div className="map-dossier-container">
          <MapView attorney={callData.attorney} />
          <AttorneyDossier attorney={callData.attorney} />
        </div>
        <button className="back-button" onClick={() => setShowAttorneyInfo(false)}>
          Back to Start
        </button>
      </div>
    )}

    {showCallSummary && callData && (
      <div className="call-summary-container">
        <CallSummary data={callData.summary} />
        <button className="back-button" onClick={() => setShowCallSummary(false)}>
          Back to Start
        </button>
      </div>
    )}
  </>
);

function App() {
  const location = useLocation();
  const [callActive, setCallActive] = useState(false)
  const [showAttorneyInfo, setShowAttorneyInfo] = useState(false)
  const [showCallSummary, setShowCallSummary] = useState(false)
  const [callData, setCallData] = useState(null)
  const [subdomain, setSubdomain] = useState('default')
  const [isAttorneySubdomain, setIsAttorneySubdomain] = useState(false)
  const [attorneyProfile, setAttorneyProfile] = useState(null)
  const [isDevelopment, setIsDevelopment] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [availableSubdomains, setAvailableSubdomains] = useState(['default'])
  const [isDarkTheme, setIsDarkTheme] = useState(true)
  const [showSubdomains, setShowSubdomains] = useState(false)
  const [selectedPracticeArea, setSelectedPracticeArea] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [activeConfigTab, setActiveConfigTab] = useState('firm');
  const [showAuthOverlay, setShowAuthOverlay] = useState(false);

  // Add missing state variables
  const [firmName, setFirmName] = useState('Smith & Associates, LLP');
  const [logoUrl, setLogoUrl] = useState('');
  const [state, setState] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#2c3e50');
  const [secondaryColor, setSecondaryColor] = useState('#3498db');
  const [buttonColor, setButtonColor] = useState('#3498db');

  // Debug log for buttonColor changes
  useEffect(() => {
    console.log('Button color changed to:', buttonColor);
  }, [buttonColor]);
  const [backgroundColor, setBackgroundColor] = useState('#f0f4f8');
  const [backgroundOpacity, setBackgroundOpacity] = useState(0.3);
  const [buttonText, setButtonText] = useState('Start Consultation');
  const [buttonOpacity, setButtonOpacity] = useState(1);
  const [previewHeight, setPreviewHeight] = useState(600);
  const [practiceDescription, setPracticeDescription] = useState('**Welcome to our legal practice**\n\nOur team of experienced attorneys is dedicated to providing you with exceptional legal representation. We combine expertise with a client-focused approach to help you navigate complex legal challenges.\n\n### How we can help:\n- Personalized legal solutions\n- Clear communication throughout your case\n- Decades of combined experience');
  const [welcomeMessage, setWelcomeMessage] = useState('Hello, I\'m an AI assistant from Smith & Associates. How can I help you today?');
  const [informationGathering, setInformationGathering] = useState('To better assist you, I\'ll need a few details about your situation.');
  const [attorneyName, setAttorneyName] = useState('John Smith');
  const [practiceAreaBackgroundOpacity, setPracticeAreaBackgroundOpacity] = useState(0.2);
  const [textBackgroundColor, setTextBackgroundColor] = useState('#634C38');

  const [firmUrl, setFirmUrl] = useState('');
  const [isUrlLoading, setIsUrlLoading] = useState(false);

  const iframeRef = useRef(null);

  // Practice areas configuration
  const practiceAreas = {
    'Personal Injury': {
      questions: "I want to know the circumstances of their injury, including the date, location, and how it occurred. Was it a car accident, slip and fall, or other type of incident?",
      practiceDescription: "**Our firm specializes in personal injury law**, and we have a proven track record of success in obtaining favorable settlements and verdicts for our clients.\n\n### Our Services:\n- Car accident claims\n- Slip and fall cases\n- Medical malpractice\n- Workplace injuries\n\nWe work on a contingency basis - *you don't pay unless we win*.",
      welcomeMessage: "Welcome to our personal injury consultation. I'm your virtual legal assistant and I'm here to help gather information about your case.",
      informationGathering: "I want to know the circumstances of your injury, including the date, location, and how it occurred. Was it a car accident, slip and fall, or other type of incident?"
    },
    'Family Law': {
      questions: "I need to understand the nature of their family law issue. Are they seeking a divorce, child custody, child support, alimony, or a modification of an existing order?",
      practiceDescription: "**Our firm is dedicated to helping families** navigate the complexities of family law. We handle all aspects of divorce, including property division, child custody and visitation.\n\n### Practice Areas:\n- Divorce proceedings\n- Child custody & support\n- Spousal support/alimony\n- Prenuptial agreements\n\n> We approach each case with compassion and understanding during difficult times.",
      welcomeMessage: "Welcome to our family law consultation. I'm your virtual legal assistant and I'm here to help with your family law matters.",
      informationGathering: "I need to understand the nature of your family law issue. Are you seeking a divorce, child custody, child support, alimony, or a modification of an existing order?"
    },
    'Criminal Defense': {
      questions: "I need to know the charges against the client, where they are in the criminal process, and if there are any deadlines or court dates already scheduled.",
      practiceDescription: "## Criminal Defense Experts\n\nOur **experienced criminal defense attorneys** provide aggressive representation for all criminal matters, from misdemeanors to serious felony charges.\n\n### Our Approach:\n1. Thorough case evaluation\n2. Strategic defense planning\n3. Aggressive courtroom advocacy\n4. Pursuit of best possible outcomes\n\n[Contact us](#) immediately if you've been charged with a crime.",
      welcomeMessage: "Thank you for considering our firm for your criminal defense needs. I'm here to gather some initial information about your case.",
      informationGathering: "Please tell me about the charges you're facing and where you are in the legal process. All information is confidential and protected by attorney-client privilege."
    }
  };

  // Handle practice area selection
  const handlePracticeAreaChange = (e) => {
    const area = e.target.value;
    setSelectedPracticeArea(area);

    if (area && practiceAreas[area]) {
      setWelcomeMessage(practiceAreas[area].welcomeMessage);
      setInformationGathering(practiceAreas[area].informationGathering);
      setPracticeDescription(practiceAreas[area].practiceDescription);
    }
  };

  // Handle file upload for logo
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Store the image in localStorage and get the ID
        const imageId = storeImage(reader.result);
        console.log('Stored image with ID:', imageId);
        // Set the logo URL to the image ID
        setLogoUrl(imageId);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle logo removal
  const handleRemoveLogo = () => {
    setLogoUrl('');
  };

  const goToPreview = () => {
    setShowPreview(true);
  };

  // Handle URL submission for auto-configuration
  const handleUrlSubmit = (e) => {
    e.preventDefault();

    if (!firmUrl) {
      alert('Please enter a URL');
      return;
    }

    setIsUrlLoading(true);

    // In a real implementation, this would call an API to scrape the website
    // For now, we'll simulate the process with a timeout
    setTimeout(() => {
      // Generate simulated data based on the URL
      const domain = firmUrl.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
      const generatedFirmName = domain
        .split(/[.-]/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ') + " Law";

      // Generate a practice description based on the domain
      let generatedDescription = "Specializing in various legal matters with a client-focused approach to help you navigate complex legal challenges.";

      // Set practice areas based on the domain keywords
      const domainLower = domain.toLowerCase();

      if (domainLower.includes('injury') || domainLower.includes('accident')) {
        setSelectedPracticeArea('Personal Injury');
        generatedDescription = "We handle all types of personal injury cases, including car accidents, slip and falls, and medical malpractice. Our experienced team will fight for the compensation you deserve.";
      } else if (domainLower.includes('family') || domainLower.includes('divorce')) {
        setSelectedPracticeArea('Family Law');
        generatedDescription = "Our compassionate family law attorneys provide guidance through divorce, custody matters, and other family legal issues with sensitivity and expertise.";
      } else if (domainLower.includes('criminal') || domainLower.includes('defense')) {
        setSelectedPracticeArea('Criminal Defense');
        generatedDescription = "Our criminal defense team provides aggressive representation for all criminal matters, from misdemeanors to serious felony charges.";
      }

      // Update state with generated information
      setFirmName(generatedFirmName);
      setPracticeDescription(generatedDescription);

      if (practiceAreas[selectedPracticeArea]) {
        setWelcomeMessage(practiceAreas[selectedPracticeArea].welcomeMessage);
        setInformationGathering(practiceAreas[selectedPracticeArea].informationGathering);
      }

      setIsUrlLoading(false);
    }, 1500);
  };

  // Helper functions for color handling
  const hexToRgb = (hex) => {
    // Remove # if present
    hex = hex.replace('#', '');

    // Parse hex values
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return `${r}, ${g}, ${b}`;
  };

  const getContrastColor = (hexColor) => {
    // Convert hex to RGB
    let hex = hexColor.replace('#', '');

    // Convert hex to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Calculate luminance - standard formula
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    // Return black for bright colors, white for dark colors
    return luminance > 0.5 ? '#000000' : '#ffffff';
  };

  // Theme management
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDarkTheme ? 'dark' : 'light');

    // Add or remove dark-theme class from body
    if (isDarkTheme) {
      document.body.classList.add('dark-theme');
    } else {
      document.body.classList.remove('dark-theme');
    }
  }, [isDarkTheme]);

  const toggleTheme = () => {
    setIsDarkTheme(prev => !prev);
  };

  // Use useEffect to determine subdomain and initialize
  useEffect(() => {
    debug.log('App component mounted');

    // Initialize and verify Supabase configuration
    initializeSupabaseConfig();

    // Test Supabase connection with our new test function
    logSupabaseConfig();
    verifySupabaseConfig().then(result => {
      if (result.success) {
        console.log('Supabase is properly configured and connected!', result.data);
        if (result.usingFallback) {
          console.warn('Using fallback Supabase configuration. Update your .env.development file for a better development experience.');
        }
      } else {
        console.warn('Supabase connection failed:', result.error);

        if (result.useMockData) {
          console.info('The application will use mock data for development');
          console.info('This is fine for local development, but you should fix the Supabase connection for production');

          // Set a flag to indicate we're using mock data
          window.USING_MOCK_DATA = true;
        }
      }
    });

    // Initialize React DevTools global hook for LegalScout
    if (window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
      window.__REACT_DEVTOOLS_GLOBAL_HOOK__.legalScout = {};
    }

    // Determine if we're in development environment
    const isDev = import.meta.env.MODE === 'development' ||
                 window.location.hostname === 'localhost' ||
                 window.location.hostname === '127.0.0.1';
    setIsDevelopment(isDev);

    // Get subdomain using our utility
    const subdomainValue = getCurrentSubdomain();
    debug.log('Subdomain detected:', subdomainValue);
    setSubdomain(subdomainValue);

    // Check if this is an attorney subdomain
    // Only consider it an attorney subdomain if it's not default, www, or empty
    const isAttorneySub = subdomainValue !== 'default' &&
                         subdomainValue !== 'www' &&
                         subdomainValue !== '' &&
                         subdomainValue !== null;
    setIsAttorneySubdomain(isAttorneySub);
    debug.log('Is attorney subdomain:', isAttorneySub, 'Subdomain value:', subdomainValue);

    // Get attorney profile based on subdomain
    const loadAttorneyProfile = async () => {
      setIsLoading(true);
      try {
        const profile = await getAttorneyConfigAsync(subdomainValue);
        debug.log('Attorney profile loaded:', profile);
        setAttorneyProfile(profile);
      } catch (error) {
        console.error('Error loading attorney profile:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAttorneyProfile();

    return () => {
      debug.log('App component unmounted');
    };
  }, []);

  // Setup subdomain testing UI in development
  useEffect(() => {
    if (!isDevelopment) return;

    // Fetch available subdomains from Supabase
    const fetchSubdomains = async () => {
      try {
        // Import supabase client
        const { supabase } = await import('./lib/supabase');

        // Fetch all attorney subdomains from the database
        const { data, error } = await supabase
          .from('attorneys')
          .select('subdomain')
          .not('subdomain', 'is', null);

        if (error) {
          console.warn('Error fetching attorney subdomains:', error);
          setAvailableSubdomains(['default']);
          return;
        }

        // Extract subdomain values from the data
        const subdomains = data.map(item => item.subdomain).filter(Boolean);

        debug.log('Available subdomains for testing:', subdomains);
        setAvailableSubdomains(['default', ...subdomains]);
      } catch (error) {
        console.warn('Error setting up subdomain testing, using default only:', error);
        setAvailableSubdomains(['default']);
      }
    };

    fetchSubdomains();
  }, [isDevelopment]);

  const startCall = () => {
    debug.log('Call started', { timestamp: new Date().toISOString() });
    trackUserJourney('call_started');
    setShowAttorneyInfo(false);
    setShowCallSummary(false);
    setCallActive(true);
  }

  const handleGetStarted = () => {
    // Show the auth overlay when Get Started is clicked
    console.log('handleGetStarted called in App.jsx');
    setShowAuthOverlay(true);
  }

  // Expose handleGetStarted to window object for access from other components
  useEffect(() => {
    window.handleGetStarted = handleGetStarted;

    return () => {
      // Clean up when component unmounts
      delete window.handleGetStarted;
    };
  }, []);

  const handleAuthSuccess = (attorneyData) => {
    // Handle successful authentication and attorney account creation
    console.log('Attorney account created:', attorneyData);

    // Check if this is development mode
    const isDevelopmentMode = attorneyData && attorneyData.id && attorneyData.id.toString().startsWith('dev-');

    if (isDevelopmentMode) {
      console.log('Development mode detected. Redirecting to dashboard...');
      // Close the auth overlay and redirect to dashboard
      setShowAuthOverlay(false);
      // Redirect to dashboard
      window.location.href = `/dashboard`;
    } else {
      // Redirect to dashboard
      window.location.href = `/dashboard`;
    }
  }

  const endCall = (data) => {
    debug.log('Call ended', { timestamp: new Date().toISOString(), data });
    trackUserJourney('call_ended');
    setCallActive(false);

    if (data && data.attorney) {
      setShowAttorneyInfo(true);
      setShowCallSummary(false);
      setCallData(data);
    }
    else if (data && data.summary) {
      setShowAttorneyInfo(false);
      setShowCallSummary(true);
      setCallData(data);
    }
    else {
      setShowAttorneyInfo(false);
      setShowCallSummary(false);
    }
  }

  // Debug the logo URL whenever it changes
  useEffect(() => {
    if (logoUrl) {
      console.log('[App.jsx] Current logoUrl:', logoUrl);
    }
  }, [logoUrl]);

  return (
    <div className="app-wrapper">
      <AnimatedBackground />
      <header className="header">
        <div className="logo-container">
          <Link to="/" aria-label="Go to home page">
            <img src="/nav_logo.webp" alt="LegalScout Logo" className="logo" />
          </Link>
        </div>
        <Navbar isDarkTheme={isDarkTheme} />
        {/* Sign In Button - conditionally rendered based on current path */}
        {location.pathname === '/demo' && (
          <SignInButton onClick={() => setShowAuthOverlay(true)} />
        )}
        <ThemeToggle isDark={isDarkTheme} onToggle={toggleTheme} />
      </header>

      <main className="main-content-layer">
        <Routes>
          <Route
            path="/"
            element={
              <Home
                isLoading={isLoading}
                callActive={callActive}
                showAttorneyInfo={showAttorneyInfo}
                showCallSummary={showCallSummary}
                attorneyProfile={attorneyProfile}
                startCall={startCall}
                endCall={endCall}
                callData={callData}
                subdomain={subdomain}
                setShowAttorneyInfo={setShowAttorneyInfo}
                setShowCallSummary={setShowCallSummary}
                buttonText={buttonText}
                isAttorneySubdomain={isAttorneySubdomain}
              />
            }
          />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<div>Contact Page Coming Soon</div>} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/test" element={<TestComponent />} />
          <Route path="/subdomain-test" element={<SubdomainTestPage />} />
          <Route path="/complete-profile" element={<CompleteProfile />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/crm-demo" element={<CrmDemo />} />
          <Route path="/demo" element={
            <Suspense fallback={
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading demo interface...</p>
              </div>
            }>
              <SimpleDemoPage
                firmName={firmName}
                logoUrl={logoUrl}
                state={state}
                primaryColor={primaryColor}
                secondaryColor={secondaryColor}
                buttonColor={buttonColor}
                setButtonColor={setButtonColor}
                backgroundColor={backgroundColor}
                backgroundOpacity={backgroundOpacity}
                welcomeMessage={welcomeMessage}
                informationGathering={informationGathering}
                practiceDescription={practiceDescription}
                previewHeight={previewHeight}
                setPreviewHeight={setPreviewHeight}
                attorneyName={attorneyName}
                selectedPracticeArea={selectedPracticeArea}
                handlePracticeAreaChange={handlePracticeAreaChange}
                showPreview={showPreview}
                setShowPreview={setShowPreview}
                handleLogoUpload={handleLogoUpload}
                handleRemoveLogo={handleRemoveLogo}
                practiceAreas={practiceAreas}
                activeConfigTab={activeConfigTab}
                setActiveConfigTab={setActiveConfigTab}
                buttonText={buttonText}
                setButtonText={setButtonText}
                buttonOpacity={buttonOpacity}
                setButtonOpacity={setButtonOpacity}
                practiceAreaBackgroundOpacity={practiceAreaBackgroundOpacity}
                setPracticeAreaBackgroundOpacity={setPracticeAreaBackgroundOpacity}
                textBackgroundColor={textBackgroundColor}
                setTextBackgroundColor={setTextBackgroundColor}
                goToPreview={goToPreview}
                setFirmName={setFirmName}
                setAttorneyName={setAttorneyName}
                setPracticeDescription={setPracticeDescription}
                setState={setState}
                setWelcomeMessage={setWelcomeMessage}
                setInformationGathering={setInformationGathering}
                setPrimaryColor={setPrimaryColor}
                setSecondaryColor={setSecondaryColor}
                setBackgroundColor={setBackgroundColor}
                setBackgroundOpacity={setBackgroundOpacity}
                iframeRef={iframeRef}
                firmUrl={firmUrl}
                setFirmUrl={setFirmUrl}
                isLoading={isUrlLoading}
                handleUrlSubmit={handleUrlSubmit}
                isDarkTheme={isDarkTheme}
                handleGetStarted={handleGetStarted}
              />
            </Suspense>
          } />
          <Route path="/demo/preview" element={
            <Suspense fallback={
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading preview...</p>
              </div>
            }>
              <PreviewPage
                firmName={firmName}
                attorneyName={attorneyName}
                darkMode={isDarkTheme}
                onToggleDarkMode={() => setIsDarkTheme(!isDarkTheme)}
              />
            </Suspense>
          } />

          <Route path="/preview" element={
            <Suspense fallback={
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading preview...</p>
              </div>
            }>
              <SimplifiedPreview
                firmName={firmName}
                primaryColor={primaryColor}
                secondaryColor={secondaryColor}
                buttonColor={buttonColor}
                backgroundColor={backgroundColor}
                backgroundOpacity={backgroundOpacity}
                practiceDescription={practiceDescription}
                welcomeMessage={welcomeMessage}
                informationGathering={informationGathering}
                theme={isDarkTheme ? 'dark' : 'light'}
                logoUrl={logoUrl || "/PRIMARY CLEAR.png"}
                buttonText={buttonText || "Start Consultation"}
                buttonOpacity={buttonOpacity}
                practiceAreaBackgroundOpacity={practiceAreaBackgroundOpacity}
                textBackgroundColor={textBackgroundColor}
                mascot="/PRIMARY CLEAR.png"
                vapiInstructions="You are a general legal assistant. Collect basic information about the client's legal needs and help them find an appropriate attorney."
              />
            </Suspense>
          } />
          <Route path="/simple-preview" element={
            <SimplifiedPreview
              firmName={firmName}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              buttonColor={buttonColor}
              backgroundColor={backgroundColor}
              backgroundOpacity={backgroundOpacity}
              practiceDescription={practiceDescription}
              welcomeMessage={welcomeMessage}
              informationGathering={informationGathering}
              theme={isDarkTheme ? 'dark' : 'light'}
              logoUrl={logoUrl || "/PRIMARY CLEAR.png"}
              buttonText={buttonText || "Start Consultation"}
              buttonOpacity={buttonOpacity}
              practiceAreaBackgroundOpacity={practiceAreaBackgroundOpacity}
              textBackgroundColor={textBackgroundColor}
              mascot="/PRIMARY CLEAR.png"
              vapiInstructions="You are a general legal assistant. Collect basic information about the client's legal needs and help them find an appropriate attorney."
            />
          } />

          {/* Preview frame for dashboard */}
          <Route path="/preview-frame" element={
            <EnhancedPreviewNew
              firmName="Your Law Firm"
              titleText="Your Law Firm"
              primaryColor="#4B74AA"
              secondaryColor="#2C3E50"
              backgroundColor="#1a1a1a"
              backgroundOpacity={0.9}
              buttonText="Start Consultation"
              buttonOpacity={1}
              practiceAreaBackgroundOpacity={0.1}
              textBackgroundColor="#634C38"
              welcomeMessage="Hello! I'm Scout, your legal assistant. How can I help you today?"
              informationGathering="Tell me about your situation, and I'll help find the right solution for you."
              logoUrl="/PRIMARY CLEAR.png"
              mascot="/PRIMARY CLEAR.png"
              theme="dark"
            />
          } />

          {/* Test route for preview frame debugging */}
          <Route path="/preview-frame-test" element={
            <div style={{
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '20px'
            }}>
              <h1>Preview Frame Test</h1>
              <p>This page is for testing the preview frame directly.</p>
              <div style={{
                width: '100%',
                maxWidth: '800px',
                height: '600px',
                border: '2px solid #4B74AA',
                borderRadius: '8px',
                overflow: 'hidden'
              }}>
                <iframe
                  src="/preview-frame"
                  title="Preview Frame Test"
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none'
                  }}
                />
              </div>
              <div>
                <h2>Troubleshooting</h2>
                <ul>
                  <li>If the preview appears here but not in the dashboard, there's likely a CSS or layout issue in the dashboard.</li>
                  <li>If the preview doesn't appear here either, there might be an issue with the preview component itself.</li>
                </ul>
              </div>
            </div>
          } />
        </Routes>
      </main>

      {isDevelopment && (
        <>
          <button
            className="subdomain-toggle-button"
            onClick={() => setShowSubdomains(!showSubdomains)}
          >
            Subdomains
          </button>
          {showSubdomains && (
            <div className="test-subdomains-container">
              <TestSubdomains availableSubdomains={availableSubdomains} />
            </div>
          )}
        </>
      )}

      {/* Auth Overlay */}
      <AuthOverlay
        isOpen={showAuthOverlay}
        onClose={() => setShowAuthOverlay(false)}
        onSuccess={handleAuthSuccess}
      />
    </div>
  );
}

export default withDevTools(App, {
  displayName: 'LegalScoutApp',
  type: 'container',
  description: 'Main application container that manages call state',
  responsibleFor: ['call initiation', 'layout management', 'state control']
});